import { AvatarSize } from '../avatar/avatar.types';
import { CSSObject } from '@emotion/react';
import {
  StatusBadge,
  StatusBadgeColor,
  StatusBadgeSize,
} from '../../badges/statusBadge';
import Box from '@mui/material/Box';
import { useTheme } from '../../../../theme';
import type { IconName } from '@doxyme/icons';

import type { JSX } from 'react';

export type AvatarBadgeProps = {
  badgeColor: StatusBadgeColor;
  statusBadgeIconName?: IconName;
  size: AvatarSize;
  disabled?: boolean;
};

const BADGE_SIZES: Record<
  AvatarSize,
  { withIcon: StatusBadgeSize; withoutIcon: StatusBadgeSize }
> = {
  '3xl': { withIcon: '3xl', withoutIcon: '2xl' },
  '2xl': { withIcon: '2xl', withoutIcon: 'xl' },
  xl: { withIcon: 'lg', withoutIcon: 'md' },
  lg: { withIcon: 'lg', withoutIcon: 'sm' },
  md: { withIcon: 'sm', withoutIcon: 'sm' },
  sm: { withIcon: 'sm', withoutIcon: 'sm' },
};

const BADGE_POSITIONS: Record<
  AvatarSize,
  { withIcon: CSSObject; withoutIcon: CSSObject }
> = {
  '3xl': {
    withIcon: { position: 'absolute', bottom: -4, right: -4 },
    withoutIcon: { position: 'absolute', bottom: -4, right: -4 },
  },
  '2xl': {
    withIcon: { position: 'absolute', bottom: -4, right: -4 },
    withoutIcon: { position: 'absolute', bottom: -4, right: -4 },
  },
  xl: {
    withIcon: { position: 'absolute', bottom: -1, right: -1 },
    withoutIcon: { position: 'absolute', bottom: -1, right: -1 },
  },
  lg: {
    withIcon: { position: 'absolute', bottom: -2, right: -2 },
    withoutIcon: { position: 'absolute', bottom: -2, right: -2 },
  },
  md: {
    withIcon: { position: 'absolute', bottom: -2, right: -2 },
    withoutIcon: { position: 'absolute', bottom: -2, right: -2 },
  },
  sm: {
    withIcon: { position: 'absolute', bottom: -2, right: -2 },
    withoutIcon: { position: 'absolute', bottom: -2, right: -2 },
  },
};

export const AvatarBadge = ({
  badgeColor,
  statusBadgeIconName,
  size,
  disabled = false,
}: AvatarBadgeProps): JSX.Element => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        ...BADGE_POSITIONS[size][
          statusBadgeIconName ? 'withIcon' : 'withoutIcon'
        ],

        '::after': disabled
          ? {
              content: '""',
              backgroundColor: theme.interactiveElement.disabled,
              position: 'absolute',
              inset: 0,
              borderRadius: theme.shape.radius100,
              zIndex: 1,
            }
          : undefined,
      }}
    >
      <StatusBadge
        color={badgeColor}
        size={
          BADGE_SIZES[size][statusBadgeIconName ? 'withIcon' : 'withoutIcon']
        }
        hasBorder
        iconName={statusBadgeIconName}
        css={{
          display: 'block',
        }}
      />
    </Box>
  );
};
